import { useMemo } from 'react'
import { useSidebarProvider } from '../contexts/Sidebar'
import {
  buildUnifiedBreadcrumb,
  type BreadcrumbOptions,
} from '../utils/breadcrumb'

/**
 * Breadcrumb Hooks for NotePlan Web
 *
 * These hooks provide easy-to-use breadcrumb functionality for React components.
 * They handle sidebar entries retrieval, memoization, and type safety automatically.
 *
 * Key benefits:
 * - Automatic memoization for performance
 * - Built-in null/undefined safety
 * - Consistent with sidebar UI
 * - Configurable options
 * - Conditional enabling support
 *
 * Migration guide:
 * Before: Complex manual breadcrumb logic with useMemo, getBreadcrumb, etc.
 * After: Single hook call with automatic handling
 *
 * Performance: Uses sidebar entries (lightweight) instead of cache queries (heavy)
 */

/**
 * Custom hook for generating breadcrumb items from a note's recordName.
 * Handles sidebar entries retrieval, memoization, and type safety.
 *
 * @param recordName - The record name of the note to generate breadcrumb for
 * @param options - Optional breadcrumb configuration options
 * @param enabled - Whether breadcrumb generation is enabled (default: true)
 * @returns Array of breadcrumb path segments
 *
 * @example
 * ```tsx
 * // Basic usage for note headers
 * const breadcrumbItems = useBreadcrumb(note.recordName)
 *
 * // With custom options
 * const breadcrumbItems = useBreadcrumb(note.recordName, {
 *   includeRoot: true,
 *   excludeFolders: ['temp']
 * })
 *
 * // Conditionally enabled
 * const breadcrumbItems = useBreadcrumb(note.recordName, {}, showBreadcrumb)
 *
 * // Usage in component
 * function NoteComponent({ note, showBreadcrumb }) {
 *   const breadcrumbItems = useBreadcrumb(note.recordName, {}, showBreadcrumb)
 *
 *   return (
 *     <div>
 *       {breadcrumbItems.map((crumb, index) => (
 *         <span key={index}>{crumb} / </span>
 *       ))}
 *       <h1>{note.title}</h1>
 *     </div>
 *   )
 * }
 * ```
 */
export function useBreadcrumb(
  recordName: string | undefined,
  options: Omit<BreadcrumbOptions, 'format'> = {},
  enabled = true
): string[] {
  const { sidebarEntries } = useSidebarProvider()

  return useMemo(() => {
    if (
      !enabled ||
      !recordName ||
      !sidebarEntries ||
      sidebarEntries.length === 0
    ) {
      return []
    }

    const result = buildUnifiedBreadcrumb(sidebarEntries, recordName, {
      ...options,
      format: 'array', // Always return array for consistency in hook
    })

    return Array.isArray(result) ? result : []
  }, [enabled, recordName, sidebarEntries, options])
}

/**
 * Specialized hook for breadcrumb strings (joined path).
 * Useful when you need the breadcrumb as a single string rather than an array.
 *
 * @param recordName - The record name of the note
 * @param separator - String to join breadcrumb segments (default: '/')
 * @param options - Optional breadcrumb configuration options
 * @param enabled - Whether breadcrumb generation is enabled (default: true)
 * @returns Joined breadcrumb path as string
 *
 * @example
 * ```tsx
 * const breadcrumbPath = useBreadcrumbPath(note.recordName)
 * // Returns: "Projects/Work/Team Planning"
 *
 * const breadcrumbPath = useBreadcrumbPath(note.recordName, ' > ')
 * // Returns: "Projects > Work > Team Planning"
 * ```
 */
export function useBreadcrumbPath(
  recordName: string | undefined,
  separator = '/',
  options: Omit<BreadcrumbOptions, 'format' | 'separator'> = {},
  enabled = true
): string {
  const breadcrumbItems = useBreadcrumb(recordName, options, enabled)

  return useMemo(() => {
    if (breadcrumbItems.length === 0) {
      return ''
    }
    return breadcrumbItems.join(separator)
  }, [breadcrumbItems, separator])
}
